import type { ReactNode } from "react";
import { AutofillBadge } from "./AutofillBadge";
import styles from "./CheckboxCard.module.css";

interface Props {
  label: string;
  /** Optional secondary line under the label. */
  description?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Field name to look up autofill state — same key as PackageFormData. */
  autofilledFor?: string;
  autofilledFields?: Set<string>;
  /** Conditional sub-content rendered inside the card when checked
   *  (e.g. stage details textarea when "Stage Available" is ticked). */
  children?: ReactNode;
  /** Render `children` whatever the tick says: the caller has already
   *  decided what shows (the questions waiting on a pack question, some
   *  of which are asked while it is unticked). */
  childrenDecided?: boolean;
}

/**
 * Bordered checkbox card — used 5+ times across ContentStep + VenueStep.
 * Centralises the layout so spacing/checkbox styling stays consistent.
 */
export function CheckboxCard({
  label,
  description,
  checked,
  onChange,
  autofilledFor,
  autofilledFields,
  children,
  childrenDecided = false,
}: Props): React.JSX.Element {
  return (
    <div className={styles.card}>
      <label
        className={`${styles.label} ${description ? styles.described : ""}`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className={styles.checkbox}
        />
        <div>
          <span className={styles.text}>
            {label}
            {autofilledFor && autofilledFields && (
              <AutofillBadge field={autofilledFor} autofilledFields={autofilledFields} />
            )}
          </span>
          {description && <span className={styles.description}>{description}</span>}
        </div>
      </label>
      {(checked || childrenDecided) && children}
    </div>
  );
}
